import { Fragment, useState, useMemo, useEffect, type FormEvent } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useExtensions, useCreateExtension, useDeleteExtension, useCreateExtensionRange,
  useTenant, type RangeResult,
} from '../../lib/api/hooks.js';
import { useAdminGroups } from '../../lib/api/hooks-extension.js';
import { useRealtime } from '../../lib/api/realtime.js';
import {
  StatusBadge, TableSkeleton, Empty, ErrorBanner, Badge, Tabs, Tab, SectionHead, SubHead, Chip, ChipList,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { Switch } from '../../components/ui/Form.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useExtensionTypes, useTypeFeatures } from '../../lib/api/hooks-products.js';
import { RangeCredentials } from './RangeCredentials.js';
import { PresenceCell, presenceOf, deviceOf } from './ExtensionPresence.js';
import { ExtensionQuickLook } from './ExtensionQuickLook.js';
import { relativeTime } from '../../lib/format.js';
import { ExtensionDetail, type SeatLink } from '../manage/ExtensionDetail.js';
import { useSubroute, childSubroute, SubrouteProvider } from '../../lib/subroute.js';
import type { Extension } from '@arcnet/contracts';
import { confirmAction } from '../../components/ui/Confirm.js';
import { usePlatformFeatures } from '../../lib/api/hooks-features.js';
import { useTeams, useTeamsStatus } from '../../lib/api/hooks-teams.js';
import { TeamsStatusCell } from '../manage/TeamsPresence.js';

/**
 * The seat list.
 *
 * Rebuilt around the question people actually open it with, which is not "what
 * is extension 1004's caller ID" — it is "who is online, and who should be but
 * isn't". Presence therefore leads, and the columns that used to fill the
 * width (caller ID, max concurrent calls) moved to the seat's own page, where
 * somebody looking for them is already going.
 *
 * One list, then one seat (14 September 2026). This was three columns -- a
 * filter rail, the seats, and the open seat in whatever width was left -- under
 * a row of counters that repeated the rail's own filters. The owner found it
 * hard to find their way around, and the seat's twelve tabs did not fit its
 * third of the screen, so most of them were out of sight. Now the counts are
 * the filters, and a seat opens on a page of its own with every section listed.
 *
 * Reused in two places: standalone, and as a tab inside an account. `tenantId`
 * is passed explicitly when embedded so the tab always shows the account in the
 * URL rather than whatever tenant happens to be selected elsewhere — otherwise
 * a support link could open the wrong customer's data.
 */

type Presence = 'all' | 'online' | 'offline' | 'never' | 'forwarded' | 'dnd';

/**
 * Who to show, by what is happening to their calls. Forwarded and DND are here
 * because the account-wide forwarding list went when forwarding moved onto the
 * extension (B4) -- "who is forwarded somewhere" is still a question worth one
 * click, and now it is a filter on the seats rather than a screen of its own.
 */
const PRESENCE: ReadonlyArray<readonly [Presence, string]> = [
  ['all', 'All'], ['online', 'Online'], ['offline', 'Offline'],
  ['never', 'Never seen'], ['forwarded', 'Forwarded'], ['dnd', 'Do not disturb'],
];

type Order = 'number' | 'name' | 'attention';

/** A word under each figure of the summary strip. */
const SUMMARY_SUB: Record<Presence, (n: number) => string> = {
  all: () => 'in this account',
  online: (n) => (n === 0 ? 'none right now' : 'taking calls'),
  offline: (n) => (n === 0 ? 'nothing down' : 'were registered, now gone'),
  never: (n) => (n === 0 ? 'all set up' : 'no handset yet'),
  forwarded: (n) => (n === 0 ? 'none' : 'calls go elsewhere'),
  dnd: (n) => (n === 0 ? 'none' : 'not taking calls'),
};

const isForwarded = (e: Extension) => !!(e.forward_always || e.forward_busy || e.forward_no_answer);

/** Unassigned is a filter value that cannot collide with a group id. */
const UNASSIGNED = '__unassigned__';

const seatLink = (e: Extension | undefined): SeatLink | undefined =>
  e ? { key: e.number, label: `${e.number} · ${e.display_name}` } : undefined;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function ExtensionsPage({ tenantId: propTenantId, embedded, scope = 'tenant' }: {
  tenantId?: string;
  embedded?: boolean;
  /**
   * Which console this list is inside. The MSP account record shows a seat's
   * identity and feature set; how the customer runs it day to day lives in
   * their own portal. See VIEWS in ExtensionDetail.
   */
  scope?: 'platform' | 'tenant';
} = {}) {
  const activeTenantId = useSession((s) => s.activeTenantId);
  const tenantId = propTenantId ?? activeTenantId;

  /**
   * The open seat lives in the URL, by its NUMBER: .../extensions/1001, and
   * .../extensions/1001/phone for one of its sections.
   *
   * It was component state, which meant no link could name a seat. Then it was
   * `?ext=<uuid>`, which named the seat in 36 characters nobody can read or say
   * (14 September 2026). A number is what people call a seat, and it is unique
   * within the account the path already names. An id still opens the seat, and
   * is rewritten to its number as soon as that is known.
   */
  const route = useSubroute();
  const seatKey = route.rest[0] ?? null;
  const view = route.rest[1];
  /**
   * `list` opens a seat at Details. `step` moves to the seat above or below and
   * keeps the section, so Phone setup can be checked seat after seat.
   */
  const openSeat = (number: string | null, how: 'list' | 'step' = 'list'): void => {
    // Opening a seat is navigation the back button should undo. Closing one
    // and stepping between them replace, so back returns to the list rather
    // than walking through every seat that was looked at.
    if (number === null) { route.go([], { replace: true }); return; }
    route.go(how === 'step' && view ? [number, view] : [number], { replace: how === 'step' });
  };
  const can = useSession((s) => s.can);
  const [q, setQ] = useState('');
  const [presence, setPresence] = useState<Presence>('all');
  /** The seat the quick-look panel shows; the first in the list when none is chosen. */
  const [selected, setSelected] = useState<string | null>(null);
  /** Plain number order (the default, owner 21 October 2026), A-Z under letter heads, or needs attention first. */
  const [order, setOrder] = useState<Order>('number');
  const [group, setGroup] = useState('');
  const [adding, setAdding] = useState(false);

  // Live for as long as the list is open: a phone registering moves a row from
  // offline to online in about a second, without a refresh.
  const live = useRealtime(tenantId ?? null);
  const { data, isLoading, error } = useExtensions(tenantId, { q: q || undefined, limit: 200 });
  /**
   * Which seats ring in Microsoft Teams, and each one's Teams status once the account's Microsoft 365
   * is connected (owner, 21 October 2026: "show a teams badge next to an extension if they are
   * registered on teams or have teams enabled"): a seat used through Teams has no phone registered,
   * and without this it reads as simply offline.
   */
  const teamsOn = usePlatformFeatures().data?.teams === true && can('teams:read') && !!tenantId;
  const teams = useTeams(tenantId ?? '', teamsOn).data;
  const teamsStatus = useTeamsStatus(tenantId ?? '', teamsOn && (teams?.seats.length ?? 0) > 0).data;
  const teamsSeat = (id: string) => teams?.seats.find((s) => s.extension_id === id && s.enabled);
  const groups = useAdminGroups(tenantId ?? '');
  const del = useDeleteExtension(tenantId ?? '');

  const all = useMemo(() => data?.data ?? [], [data]);
  /**
   * The three presence states PARTITION the seats: online + offline + never
   * seen = all.
   *
   * `offline` used to be `total - online`, which swallowed the never-seen
   * seats, so two seats displayed as 0 online, 2 offline and 1 never seen --
   * three numbers for two seats. A seat is a handset that stopped or one that
   * was never configured, never both. Forwarded and DND cut across all three.
   */
  /**
   * A seat's presence for the counts and filters, Teams included (owner, 21 October 2026): signed in
   * to Teams is online, signed out is offline rather than never seen, as on the Live page.
   */
  const stateOf = (e: Extension): 'online' | 'offline' | 'never' => {
    const phone = presenceOf(e).state;
    if (phone === 'online' || !teamsSeat(e.id) || !teamsStatus?.microsoft_connected) return phone;
    const p = teamsStatus.seats.find((t) => t.extension_id === e.id)?.presence;
    if (!p) return phone;
    return p.availability !== 'Offline' && p.availability !== 'PresenceUnknown' ? 'online' : 'offline';
  };

  const counts = useMemo(() => {
    const c: Record<Presence, number> = {
      all: all.length, online: 0, offline: 0, never: 0, forwarded: 0, dnd: 0,
    };
    for (const e of all) {
      c[stateOf(e)]++;
      if (isForwarded(e)) c.forwarded++;
      if (e.do_not_disturb) c.dnd++;
    }
    return c;
  }, [all, teams, teamsStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => all.filter((e) => {
    // The same three states as the counts, so "Offline" returns the number its
    // own count shows rather than that plus the never-seen seats.
    if ((presence === 'online' || presence === 'offline' || presence === 'never')
      && stateOf(e) !== presence) return false;
    if (presence === 'forwarded' && !isForwarded(e)) return false;
    if (presence === 'dnd' && !e.do_not_disturb) return false;
    if (group === UNASSIGNED && e.admin_group_id) return false;
    if (group && group !== UNASSIGNED && e.admin_group_id !== group) return false;
    return true;
  }), [all, presence, group, teams, teamsStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Attention first (UX review, 21 October 2026, mockup B): offline seats, the most recently gone at
   * the top -- "seen 3h ago" is today's problem, "seen 15d ago" a seat nobody uses -- then seats never
   * set up, then those online. "By number" keeps one plain list; "By name" groups under letters.
   */
  const sections = useMemo(() => {
    if (order === 'number') return [{ key: 'all', label: '', rows }];
    if (order === 'name') {
      const byLetter = new Map<string, Extension[]>();
      for (const e of [...rows].sort((a, b) => a.display_name.localeCompare(b.display_name, 'en-ZA', { sensitivity: 'base' }))) {
        const c = e.display_name.trim().charAt(0).toLocaleUpperCase('en-ZA');
        const k = /[A-Z]/.test(c) ? c : '#';
        byLetter.set(k, [...(byLetter.get(k) ?? []), e]);
      }
      return [...byLetter].map(([k, r]) => ({ key: k, label: k, rows: r }));
    }
    const seen = (e: Extension) => (e.registration?.last_seen_at ? Date.parse(e.registration.last_seen_at) : 0);
    const by = (s: 'offline' | 'never' | 'online') => rows.filter((e) => stateOf(e) === s);
    return [
      { key: 'offline', label: 'Offline', rows: by('offline').sort((a, b) => seen(b) - seen(a)) },
      { key: 'never', label: 'Never seen', rows: by('never') },
      { key: 'online', label: 'Online', rows: by('online') },
    ].filter((x) => x.rows.length > 0);
  }, [rows, order, teams, teamsStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  const flatRows = sections.flatMap((x) => x.rows);
  const current = flatRows.find((e) => e.id === selected) ?? flatRows[0];

  /**
   * Which seat the address names. Usually it is on the page already. One that
   * is not -- a search is typed, or the account has more seats than the page
   * holds -- is looked up by its number.
   */
  const byId = seatKey !== null && UUID.test(seatKey);
  const listed = seatKey === null ? undefined
    : all.find((e) => (byId ? e.id : e.number) === seatKey);
  const lookup = useExtensions(
    seatKey !== null && !byId && !listed && !isLoading ? tenantId ?? null : null,
    { q: seatKey ?? undefined, limit: 20 },
  );
  const seat = listed ?? lookup.data?.data.find((e) => e.number === seatKey);
  const seatId = byId ? seatKey : seat?.id ?? null;
  useEffect(() => {
    if (byId && seat) route.go([seat.number, ...route.rest.slice(1)], { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byId, seat?.number]);

  if (!tenantId) {
    return <Empty icon="accounts" title="No tenant selected"
      hint="Choose a tenant to manage its extensions." />;
  }

  /**
   * A seat replaces the list, at every width.
   *
   * Beside the list it had a third of the screen, which is how its sections
   * came to be out of sight. Stepping through seats -- the one thing the
   * side-by-side layout did well -- is the up and down buttons on the seat,
   * following the list as it was filtered.
   */
  if (seatKey !== null) {
    if (!seatId) {
      return (
        <>
          <button type="button" className="arc-crumb" onClick={() => openSeat(null)}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
            <Icon name="arrowLeft" size={15} /> Extensions
          </button>
          {isLoading || lookup.data === undefined ? (
            <div className="arc-card arc-card-body">
              <div className="arc-skeleton" style={{ height: 90 }} />
            </div>
          ) : (
            <div className="arc-card">
              <Empty icon="extension" title={`No extension ${seatKey}`}
                hint="It may have been renumbered or removed." />
            </div>
          )}
        </>
      );
    }
    const order = rows.some((e) => e.id === seatId) ? rows : all;
    const at = order.findIndex((e) => e.id === seatId);
    return (
      // Below the seat is the seat's own: which of its sections is open.
      <SubrouteProvider value={childSubroute(route, [seatKey])}>
        <ExtensionDetail tenantId={tenantId} extensionId={seatId} scope={scope}
          onBack={() => openSeat(null)}
          prev={at > 0 ? seatLink(order[at - 1]) : undefined}
          next={at >= 0 ? seatLink(order[at + 1]) : undefined}
          onStep={(number) => openSeat(number, 'step')} />
      </SubrouteProvider>
    );
  }

  const groupList = groups.data?.data ?? [];
  // A cost-centre column reading "unassigned" on every row says nothing, so
  // the column and its filter appear only once the account has cost centres.
  const hasGroups = groupList.length > 0;

  return (
    <>
      <SectionHead title="Extensions" sub="Seats, their handsets and who is online."
        action={<>
          {live === 'live' && (
            <Badge tone="ok"><span className="arc-dot" aria-hidden="true" />live</Badge>
          )}
          {can('extension:write') && (
            <button disabled={adding} type="button" className="arc-btn arc-btn-primary"
              onClick={() => setAdding((v) => !v)}>
              <Icon name="plus" size={13} />
              New extension
            </button>
          )}
        </>} />

      {error && <ErrorBanner error={error} />}
      {adding && <NewExtensionForm tenantId={tenantId} onDone={() => setAdding(false)} />}

      {/* The counts are the filter (UX review, 21 October 2026, mockup B): one summary, each figure
          a click that narrows the list to it. */}
      <div className="arc-card arc-stat-group arc-stat-picks" style={{ marginBottom: 14 }} role="group" aria-label="Show">
        {PRESENCE.map(([k, label]) => (
          <button key={k} type="button" className="arc-stat arc-stat-pick" aria-pressed={presence === k}
            onClick={() => setPresence(presence === k && k !== 'all' ? 'all' : k)}>
            <div className="arc-stat-body">
              <div className="arc-stat-label">{label}</div>
              <div className="arc-stat-value"
                style={k === 'online' && counts[k] > 0 ? { color: 'var(--arc-ok)' }
                  : k === 'offline' && counts[k] > 0 ? { color: 'var(--arc-danger)' } : undefined}>{counts[k]}</div>
              <div className="arc-stat-sub">{SUMMARY_SUB[k](counts[k])}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="arc-split">
      <div className="arc-card" style={{ minWidth: 0 }}>
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search number or name"
              value={q} onChange={(e) => setQ(e.target.value)}
              aria-label="Search extensions" />
          </div>

          {hasGroups && (
            <select className="arc-select" style={{ maxWidth: 190 }} value={group}
              aria-label="Filter by department"
              onChange={(e) => setGroup(e.target.value)}>
              <option value="">All departments</option>
              <option value={UNASSIGNED}>Unassigned</option>
              {groupList.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          )}

          <select className="arc-select" style={{ maxWidth: 210 }} value={order}
            aria-label="Order" onChange={(e) => setOrder(e.target.value as Order)}>
            <option value="number">By number</option>
            <option value="name">By name, A–Z</option>
            <option value="attention">Needs attention first</option>
          </select>

          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {rows.length === all.length ? `${all.length}` : `${rows.length} of ${all.length}`} shown
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table" style={{ minWidth: 0 }}>
            <colgroup>
              <col />
              <col style={{ width: 200 }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 76 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Extension</th>
                <th>Status</th>
                <th>Device</th>
                <th>Last seen</th>
                <th />
              </tr>
            </thead>
            {isLoading ? <TableSkeleton cols={5} /> : (
              <tbody>
                {sections.map((sec) => (
                  <Fragment key={sec.key}>
                    {sec.label && (
                      <tr className="arc-group-row"><td colSpan={5}>{sec.label}<span className="arc-num">{sec.rows.length}</span></td></tr>
                    )}
                    {sec.rows.map((e) => {
                      const teamsLeg = teamsSeat(e.id);
                      const tp = teamsStatus?.seats.find((s) => s.extension_id === e.id)?.presence;
                      const d = deviceOf(e.registration?.user_agent);
                      const meta = [hasGroups ? (e.admin_group_name ?? 'No department') : null, e.extension_type_name].filter(Boolean).join(' · ');
                      const st = stateOf(e);
                      return (
                        <tr key={e.id} className="arc-clickable" tabIndex={0} aria-selected={e.id === current?.id}
                          data-ext={e.id}
                          onClick={() => setSelected(e.id)}
                          onDoubleClick={() => openSeat(e.number)}
                          onKeyDown={(evt) => {
                            if (evt.key === 'Enter') { evt.preventDefault(); openSeat(e.number); }
                            if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
                              evt.preventDefault();
                              const flat = sections.flatMap((x) => x.rows);
                              const i = flat.findIndex((x) => x.id === e.id) + (evt.key === 'ArrowDown' ? 1 : -1);
                              const next = flat[i];
                              if (next) {
                                setSelected(next.id);
                                const el = evt.currentTarget.closest('tbody')?.querySelector(`[data-ext="${next.id}"]`);
                                if (el instanceof HTMLElement) el.focus();
                              }
                            }
                          }}>
                          <td>
                            <div className="arc-row" style={{ gap: 10 }}>
                              <span className="arc-ext-num">{e.number}</span>
                              {teamsLeg && (
                                <span className="arc-muted" title="Microsoft Teams" aria-label="Microsoft Teams"
                                  style={{ display: 'inline-flex', marginLeft: -4 }}>
                                  <Icon name="teams" size={15} />
                                </span>
                              )}
                              <div className="arc-stack" style={{ minWidth: 0 }}>
                                <span className="arc-truncate" style={{ fontWeight: 550 }}>{e.display_name}</span>
                                {meta && <span className="arc-subtle arc-truncate" style={{ fontSize: 12 }}>{meta}</span>}
                              </div>
                              {e.status !== 'active' && <StatusBadge status={e.status} />}
                              {e.do_not_disturb && <Badge tone="warn">DND</Badge>}
                            </div>
                          </td>
                          <td>
                            {teamsLeg ? (
                              <div className="arc-stack" style={{ gap: 4 }}>
                                {presenceOf(e).state !== 'never' && <PresenceCell ext={e} />}
                                <TeamsStatusCell connected={!!teamsStatus?.microsoft_connected} presence={tp} />
                              </div>
                            ) : <PresenceCell ext={e} />}
                          </td>
                          <td>
                            {d ? (
                              <div className="arc-stack" style={{ minWidth: 0 }}
                                title={[e.registration?.user_agent, e.registration?.contact_ip].filter(Boolean).join(' · ')}>
                                <span className="arc-truncate">{d.name}</span>
                                {d.kind && <span className="arc-subtle arc-truncate" style={{ fontSize: 12 }}>{d.kind}</span>}
                              </div>
                            ) : teamsLeg ? (
                              <div className="arc-stack">
                                <span>Microsoft Teams</span>
                                <span className="arc-subtle" style={{ fontSize: 12 }}>rings in Teams</span>
                              </div>
                            ) : <span className="arc-subtle">not set up</span>}
                          </td>
                          <td className="arc-muted arc-num" style={{ fontSize: 12.5 }}>
                            {st === 'online' ? 'now' : e.registration?.last_seen_at ? relativeTime(e.registration.last_seen_at) : 'never'}
                          </td>
                          <td className="arc-td-right">
                            <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                              {can('extension:delete') && (
                                <RowAction kind="delete" label={`Delete extension ${e.number}`}
                                  onClick={() => {
                                    void confirmAction({ title: `Delete extension ${e.number} (${e.display_name})?`, body: 'The handset will stop working immediately.', action: 'Delete', danger: true }).then((ok) => { if (ok) {
                                      del.mutate(e.id);
                                    } });
                                  }} />
                              )}
                              <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                                title={`Open ${e.number}`} aria-label={`Open extension ${e.number}`}
                                onClick={(evt) => { evt.stopPropagation(); openSeat(e.number); }}>
                                <Icon name="chevronRight" size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {data !== undefined && all.length === 0 && (
          <Empty icon="extension" title="No extensions" hint="Add one to start making calls." />
        )}
        {data !== undefined && all.length > 0 && rows.length === 0 && (
          <Empty icon="search" title="Nothing matches those filters"
            hint={hasGroups
              ? 'Choose All or clear the department filter to see the rest.'
              : 'Choose All to see the rest.'} />
        )}
      </div>
      {current && (
        <ExtensionQuickLook tenantId={tenantId} ext={current}
          teams={teamsSeat(current.id) ? {
            connected: !!teamsStatus?.microsoft_connected,
            presence: teamsStatus?.seats.find((s) => s.extension_id === current.id)?.presence,
          } : null}
          onOpen={(v) => (v ? route.go([current.number, v]) : openSeat(current.number))} />
      )}
      </div>
    </>
  );
}

/**
 * Creation, with the fields that are awkward to change later.
 *
 * Not every setting — a seat has a dozen sections of them and a wall of inputs
 * at creation is how people stop reading. These are the ones whose default is
 * wrong often enough to be worth asking: cost centre because unallocated spend
 * is what finance chases, and internal-only because the safe answer for a new
 * seat is "cannot dial out until somebody says so".
 */
function NewExtensionForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const create = useCreateExtension(tenantId);
  const createRange = useCreateExtensionRange(tenantId);
  const groups = useAdminGroups(tenantId);
  // The one SIP name every phone is given, as server and domain (6 October 2026).
  const server = useTenant(tenantId).data?.sip_registrar_host ?? null;
  const [number, setNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [internalOnly, setInternalOnly] = useState(false);
  const [typeId, setTypeId] = useState('');
  // Only the provider's staff choose a package. A customer's new seats get the
  // base package, applied by the API, so the picker is not offered to them.
  const can = useSession((s) => s.can);
  const canPackage = can('extension:package:write');
  /**
   * Single or a block. One form with a mode rather than two screens: every
   * field except the number means the same thing in both, and a second form
   * would be a second place for the type picker to drift.
   */
  const [mode, setMode] = useState<'single' | 'range'>('single');
  const [rangeTo, setRangeTo] = useState('');
  const [namePrefix, setNamePrefix] = useState('');
  const [rangeResult, setRangeResult] = useState<RangeResult | null>(null);
  const types = useExtensionTypes();
  const typeFeatures = useTypeFeatures(typeId || null);
  const [issued, setIssued] = useState<
    { username: string; realm: string; password: string } | null>(null);

  const submitRange = (e: FormEvent) => {
    e.preventDefault();
    createRange.mutate(
      {
        from: number, to: rangeTo,
        ...(namePrefix ? { display_name_prefix: namePrefix } : {}),
        ...(typeId && canPackage ? { extension_type_id: typeId } : {}),
        ...(groupId ? { admin_group_id: groupId } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      },
      {
        onSuccess: (r) => {
          setNumber(''); setRangeTo(''); setNamePrefix('');
          // Replaces the form: every password in it is visible exactly once.
          setRangeResult(r);
        },
      },
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        number, display_name: displayName,
        ...(groupId ? { admin_group_id: groupId } : {}),
        ...(typeId && canPackage ? { extension_type_id: typeId } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      },
      {
        onSuccess: (created) => {
          setNumber(''); setDisplayName(''); setGroupId('');
          // The TYPE is deliberately kept. Seats are created in runs of the
          // same kind -- five agents, then three managers -- so re-picking it
          // for every one is the fiddly part of adding a team.
          /**
           * The form stays open when a password came back.
           *
           * Closing on success is right for everything else, and wrong here:
           * this is the only moment the SIP password is visible, and a dialog
           * that vanishes with it takes the handset's configuration with it.
           */
          if (created?.sip_credential) setIssued(created.sip_credential);
          else onDone();
        },
      },
    );
  };

  /**
   * Whichever mutation is in play. A range refusal is the MORE important of the
   * two to surface -- it names the numbers that collided, and that message is
   * the entire instruction for what to do next.
   */
  /**
   * Shown while typing so a mistyped range is obvious BEFORE submitting -- the
   * difference between 1010-1029 and 1010-10029 is one keystroke and two orders
   * of magnitude.
   */
  const rangeCount = /^\d{2,6}$/.test(number) && /^\d{2,6}$/.test(rangeTo)
    ? Math.max(0, Number(rangeTo) - Number(number) + 1)
    : 0;

  const active = mode === 'range' ? createRange : create;
  const fieldErrors = active.error instanceof ApiClientError ? active.error.fieldErrors : {};

  /**
   * Shown once, in place of the form.
   *
   * A SIP password is minted when the seat is created and can only be produced
   * again under step-up, so this is the moment it has to be captured. It
   * replaces the form rather than sitting beside it: the next thing to do is
   * put these three values into a handset, not create another extension.
   */
  if (rangeResult) {
    return (
      <RangeCredentials
        result={rangeResult} server={server}
        onDone={() => { setRangeResult(null); onDone(); }}
      />
    );
  }

  if (issued) {
    return (
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SectionHead title="Extension created"
          sub="These are the credentials for the handset. The password is shown once and cannot be read back afterwards — copy it now." />
        <dl className="arc-kv">
          <dt>SIP username</dt><dd className="arc-mono">{issued.username}</dd>
          {/* The server and the domain both: the edge finds the account from the username, and the
              handset never needs the account's own domain (6 October 2026). */}
          <dt>SIP server</dt><dd className="arc-mono">{server ?? '\u2014'}</dd>
          <dt>Password</dt>
          <dd className="arc-row" style={{ gap: 8 }}>
            <span className="arc-mono" style={{ fontWeight: 600, userSelect: 'all' }}>
              {issued.password}
            </span>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => void navigator.clipboard?.writeText(issued.password)}>
              Copy
            </button>
          </dd>
        </dl>
        <div className="arc-row" style={{ gap: 8, marginTop: 14 }}>
          <button type="button" className="arc-btn arc-btn-primary"
            onClick={() => { setIssued(null); onDone(); }}>
            Done
          </button>
          <button type="button" className="arc-btn arc-btn-ghost"
            onClick={() => setIssued(null)}>
            Add another
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={mode === 'range' ? submitRange : submit}>
      <SubHead action={
        <Tabs style={{ margin: 0, borderBottom: 'none' }}>
          <Tab active={mode === 'single'} onSelect={() => setMode('single')}>Single</Tab>
          <Tab active={mode === 'range'} onSelect={() => setMode('range')}>Range</Tab>
        </Tabs>}>
        {mode === 'range' ? 'New extension range' : 'New extension'}
      </SubHead>

      <div style={{
        display: 'grid',
        gridTemplateColumns: mode === 'range'
          ? '110px 110px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)'
          : '120px minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr)',
        gap: 12, alignItems: 'start',
      }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-number">
            {mode === 'range' ? 'From' : 'Number'}
          </label>
          <input id="ext-number" className="arc-input arc-mono" inputMode="numeric"
            placeholder={mode === 'range' ? '1010' : '1003'} required value={number}
            onChange={(e) => setNumber(e.target.value)}
            aria-invalid={!!fieldErrors['number'] || !!fieldErrors['from']} />
          {fieldErrors['number'] ?? fieldErrors['from']
            ? <div className="arc-error">{fieldErrors['number'] ?? fieldErrors['from']}</div>
            : <div className="arc-hint">
                2–6 digits. 91xx is reserved.
                {mode === 'range' && ' Leading zeros are kept.'}
              </div>}
        </div>

        {mode === 'range' ? (
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="ext-to">To</label>
            <input id="ext-to" className="arc-input arc-mono" inputMode="numeric"
              placeholder="1029" required value={rangeTo}
              onChange={(e) => setRangeTo(e.target.value)}
              aria-invalid={!!fieldErrors['to']} />
            {fieldErrors['to']
              ? <div className="arc-error">{fieldErrors['to']}</div>
              : <div className="arc-hint">
                  {rangeCount > 0
                    ? `${rangeCount} extension${rangeCount === 1 ? '' : 's'}. Up to 100 at once.`
                    : 'Inclusive. Up to 100 at once.'}
                </div>}
          </div>
        ) : (
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="ext-name">Display name</label>
            <input id="ext-name" className="arc-input" placeholder="Reception"
              required value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              aria-invalid={!!fieldErrors['display_name']} />
            {fieldErrors['display_name'] && <div className="arc-error">{fieldErrors['display_name']}</div>}
          </div>
        )}

        {mode === 'range' && (
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="ext-prefix">Name prefix</label>
            <input id="ext-prefix" className="arc-input" placeholder="Agent"
              value={namePrefix} onChange={(e) => setNamePrefix(e.target.value)} />
            <div className="arc-hint">
              {namePrefix
                ? `Names become "${namePrefix} ${number || '1010'}".`
                : 'Optional. Names default to the number itself.'}
            </div>
          </div>
        )}

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-type">Extension type</label>
          {canPackage ? (
            <>
              <select id="ext-type" className="arc-select" value={typeId}
                onChange={(e) => setTypeId(e.target.value)}>
                <option value="">Base (default)</option>
                {types.data?.data.filter((t) => t.is_active).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <div className="arc-hint">
                {typeId
                  ? 'Loads the features below. Paid add-ons can be switched on per seat afterwards.'
                  : 'New seats get the Base package unless you choose another.'}
              </div>
            </>
          ) : (
            <div id="ext-type" className="arc-hint">
              New seats get the Base package. Your provider sets packages, so ask them to change one.
            </div>
          )}
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-group">Department</label>
          <select id="ext-group" className="arc-select" value={groupId}
            onChange={(e) => setGroupId(e.target.value)}>
            <option value="">Unassigned</option>
            {groups.data?.data.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <div className="arc-hint">Where this seat's spend is reported.</div>
        </div>
      </div>

      {/*
        What the chosen type actually brings, shown BEFORE the seat is created.
        The type is a product decision with a price attached, and picking one
        from a list of names alone means finding out what was bought afterwards.
      */}
      {typeId && (typeFeatures.data?.data.some((f) => f.from_type) ?? false) && (
        <div style={{ marginTop: 14 }}>
          <div className="arc-label" style={{ marginBottom: 6 }}>
            Included with this type
          </div>
          <ChipList>
            {typeFeatures.data?.data.filter((f) => f.from_type).map((f) => (
              <Chip key={f.key} title={f.description}>{f.name}</Chip>
            ))}
          </ChipList>
          <div className="arc-hint" style={{ marginTop: 6 }}>
            These come from the type, so changing the product later reaches this
            seat too. Overriding one here affects only this extension.
          </div>
        </div>
      )}

      <div className="arc-row" style={{ gap: 10, margin: '12px 0 14px' }}>
        <Switch id="ext-internal" checked={internalOnly} onChange={setInternalOnly} />
        <label htmlFor="ext-internal" style={{ fontSize: 12.5, cursor: 'pointer' }}>
          Internal calls only
          <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
            No outside line until somebody turns it on. Everything else has a
            sensible default and lives on the seat's own page.
          </span>
        </label>
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={active.isPending}>
        {active.isPending
          ? 'Creating…'
          : mode === 'range'
            ? `Create ${rangeCount > 0 ? rangeCount : ''} extension${rangeCount === 1 ? '' : 's'}`.replace('  ', ' ')
            : 'Create extension'}
      </button>
      <button type="button" className="arc-btn" style={{ marginLeft: 8 }} onClick={onDone}>Cancel</button>
    </form>
  );
}
